import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';

const HeaderWrapper = styled.header<{ scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ theme, scrolled }) => 
    scrolled ? 'rgba(255, 255, 255, 0.98)' : 'transparent'};
  box-shadow: ${({ theme, scrolled }) => 
    scrolled ? '0 2px 20px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: ${({ theme }) => theme.transitions.default};
  padding: 1rem 0;
  backdrop-filter: ${({ scrolled }) => scrolled ? 'blur(10px)' : 'none'};
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
`;

const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.black};
  font-family: ${({ theme }) => theme.fonts.logo};
  z-index: 1001;
  letter-spacing: 2px;
  text-transform: uppercase;
  
  span {
    font-weight: 300;
    font-size: 0.7em;
    display: block;
    letter-spacing: 4px;
    margin-top: -5px;
  }
`;

const NavLinks = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
    width: 70%;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.white};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: right 0.3s ease-in-out;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }
`;

const NavItem = styled.li`
  a {
    color: ${({ theme }) => theme.colors.black};
    font-weight: 500;
    padding: 0.5rem 1rem;
    cursor: pointer;
    transition: ${({ theme }) => theme.transitions.default};
    position: relative;
    text-transform: uppercase;
    font-size: 0.875rem;
    letter-spacing: 1px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background-color: ${({ theme }) => theme.colors.accent};
      transition: ${({ theme }) => theme.transitions.default};
      transform: translateX(-50%);
    }
    
    &:hover::after {
      width: 100%;
    }
    
    &.active {
      color: ${({ theme }) => theme.colors.accent};
      &::after {
        width: 100%;
      }
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const MenuIcon = styled.div<{ isOpen: boolean }>`
  width: 25px;
  height: 2px;
  background-color: ${({ theme, isOpen }) => 
    isOpen ? 'transparent' : theme.colors.black};
  position: relative;
  transition: ${({ theme }) => theme.transitions.default};
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 25px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.black};
    transition: ${({ theme }) => theme.transitions.default};
  }
  
  &::before {
    transform: ${({ isOpen }) => 
      isOpen ? 'rotate(45deg) translate(0, 0)' : 'translateY(-8px)'};
  }
  
  &::after {
    transform: ${({ isOpen }) => 
      isOpen ? 'rotate(-45deg) translate(0, 0)' : 'translateY(8px)'};
  }
`;

const navLinks = [
  { name: 'Home', to: 'home' },
  { name: 'O nas', to: 'about' },
  { name: 'Zajęcia', to: 'services' },
  { name: 'Style tańca', to: 'portfolio' },
  { name: 'Instruktorzy', to: 'instructors' },
  { name: 'Grafik', to: 'grafik' },
  { name: 'Kontakt', to: 'contact' },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <HeaderWrapper scrolled={scrolled || isOpen}>
      <NavContainer>
        <Logo>
          ADARA
          <span>DANCE STUDIO</span>
        </Logo>
        
        <MobileMenuButton onClick={toggleMenu} aria-label="Toggle menu">
          <MenuIcon isOpen={isOpen} />
        </MobileMenuButton>
        
        <NavLinks isOpen={isOpen}>
          {navLinks.map((link) => (
            <NavItem key={link.to}>
              <Link
                to={link.to}
                spy={true}
                smooth={true}
                duration={500}
                onClick={closeMenu}
                activeClass="active"
                offset={-80}
              >
                {link.name}
              </Link>
            </NavItem>
          ))}
        </NavLinks>
      </NavContainer>
    </HeaderWrapper>
  );
};

export default Header;
